// @ts-nocheck
import * as React from "react";
import { Sidebar, Topbar, AppFooter, useCurrentUser, Ic, conflictMessage, apiErrorMessage } from "./common";

// 데스크톱 방문 상세. 케어매니저 대시보드의 "오늘의 방문 (시간순)" 리스트에서
// 진입한다. 데이터는 모바일 화면(MgrVisit)이 쓰는 /api/v1/mobile/visit/:id 를
// 그대로 재사용 — 동일 페이로드이므로 별도 엔드포인트를 추가하지 않는다.
// 현장 체크인은 모바일이 맡되, 데스크톱에서도 상태 전이/보고서 진입은 가능.

const STATUS_LABEL = { planned: "예정", in_progress: "진행중", completed: "완료", cancelled: "취소" };
const STATUS_COLOR = { planned: "gray", in_progress: "orange", completed: "green", cancelled: "rose" };
const RISK_LABEL   = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR   = { critical: "rose", warning: "amber", stable: "green" };

function fmtDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("ko-KR", { dateStyle: "medium", timeStyle: "short" });
}

function humanError(status, body) {
  if (status === 401) return "로그인이 필요합니다.";
  if (status === 403) return "권한이 없습니다.";
  if (status === 404) return "방문을 찾을 수 없습니다.";
  if (status === 409) return conflictMessage(body);
  return apiErrorMessage(body, status);
}

function VisitDetail() {
  const { user } = useCurrentUser();
  const [visitId, setVisitId] = React.useState(() => new URLSearchParams(window.location.search).get("id"));
  const [data, setData] = React.useState(null);
  const [err, setErr]   = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  // URL 변경(뒤로/앞으로/sidebar 진입) 시에도 다시 로드.
  React.useEffect(() => {
    const onPop = () => setVisitId(new URLSearchParams(window.location.search).get("id"));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const load = React.useCallback(async (id) => {
    setErr(null);
    try {
      // id가 없으면 오늘 visits 중 첫 항목으로 폴백 (캔버스/딥링크 없는 진입).
      let resolvedId = id;
      if (!resolvedId) {
        const r = await fetch("/api/v1/visits", { credentials: "same-origin" });
        if (r.ok) {
          const j = await r.json();
          const pick = j.visits?.find(v => v.status === "in_progress")
                    || j.visits?.find(v => v.status === "planned")
                    || j.visits?.[0];
          resolvedId = pick?.id;
        }
      }
      if (!resolvedId) { setData({ empty: true }); return; }
      const res = await fetch(`/api/v1/mobile/visit/${resolvedId}`, { credentials: "same-origin" });
      const body = await res.json().catch(() => null);
      if (!res.ok) { setErr(humanError(res.status, body)); return; }
      setData(body);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  React.useEffect(() => { load(visitId); }, [visitId, load]);

  const patchVisit = React.useCallback(async (changes) => {
    if (!data?.visit?.id || busy) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`/api/v1/visits/${data.visit.id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ visit: changes }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) { setErr(humanError(res.status, body)); return; }
      await load(data.visit.id);
    } finally { setBusy(false); }
  }, [data?.visit?.id, busy, load]);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="manager" active="schedule"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar
            title={data?.visit ? `방문 #V${String(data.visit.id).padStart(5,'0')}` : "방문 상세"}
            sub={data?.elderly ? `${data.elderly.name} 어르신 · ${fmtDateTime(data.visit.scheduled_at)}` : ""}
          />
          <div className="main">

            <div style={{marginBottom:14, display:'flex', alignItems:'center', gap:8}}>
              <a href="/schedule" className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>‹ 일정으로</a>
              {data?.visit && (
                <a href={`/m/visit?id=${data.visit.id}`} className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>
                  모바일에서 보기 ↗
                </a>
              )}
              <div style={{flex:1}}/>
              {data?.visit && (
                <a href={`/map?focus=${data.visit.id}`} className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>
                  {Ic.map} 지도에서 보기
                </a>
              )}
            </div>

            {err && <div className="card" style={{padding:14, marginBottom:12, color:'var(--rose-500)', background:'var(--rose-50)'}}>{err}</div>}

            {!err && data === null && <div className="muted" style={{padding:30}}>불러오는 중…</div>}

            {data?.empty && <div className="muted" style={{padding:30}}>표시할 방문이 없습니다.</div>}

            {data?.visit && (() => {
              const v = data.visit;
              const e = data.elderly;
              const r = data.report;
              return (
                <>
                  <div className="card" style={{padding:'22px 26px', marginBottom:14, display:'grid', gridTemplateColumns:'1.4fr 1fr 1fr 1fr', gap:24, alignItems:'center', background:'linear-gradient(120deg, var(--orange-50) 0%, var(--bg-sub) 100%)', border:'1px solid var(--orange-100)'}}>
                    <div>
                      <div style={{fontSize:11, fontWeight:700, color:'var(--orange-700)', marginBottom:4}}>{STATUS_LABEL[v.status]} · {fmtDateTime(v.scheduled_at)}</div>
                      <div className="h1" style={{fontSize:22}}>{e.name} 어르신 · {e.age}세</div>
                      <div className="muted" style={{fontSize:12, marginTop:4}}>{v.address || e.address}</div>
                      <div style={{display:'flex', gap:6, marginTop:8}}>
                        <span className={"badge badge-" + STATUS_COLOR[v.status]}>{STATUS_LABEL[v.status]}</span>
                        <span className={"badge badge-" + RISK_COLOR[e.risk_level]}>{RISK_LABEL[e.risk_level]}</span>
                        {v.duration_mins && <span className="badge badge-gray">{v.duration_mins}분 예정</span>}
                      </div>
                    </div>
                    {[
                      { l:"체크인",   v: v.check_in_at  ? fmtDateTime(v.check_in_at)  : "—" },
                      { l:"체크아웃", v: v.check_out_at ? fmtDateTime(v.check_out_at) : "—" },
                      { l:"매니저",   v: data.manager?.name || "—" },
                    ].map((s,i) => (
                      <div key={i} style={{borderLeft:'1px solid var(--border)', paddingLeft:18}}>
                        <div className="muted" style={{fontSize:11, fontWeight:600}}>{s.l}</div>
                        <div style={{fontSize:15, fontWeight:700, color:'var(--ink-1)', marginTop:2}}>{s.v}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:14}}>
                    <div style={{display:'flex', flexDirection:'column', gap:14}}>
                      <div className="card card-pad">
                        <div className="h3" style={{marginBottom:10}}>어르신 정보</div>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 24px'}}>
                          <Field label="이름"      value={`${e.name} (${e.age}세)`}/>
                          <Field label="위험도">
                            <span className={"badge badge-" + RISK_COLOR[e.risk_level]}>{RISK_LABEL[e.risk_level]}</span>
                          </Field>
                          <Field label="주소"      value={e.address} span={2}/>
                          <Field label="건강 상태" value={e.health_status || "—"} span={2}/>
                          <Field label="보호자"    value={e.guardian_name ? `${e.guardian_name}${e.guardian_phone ? ` · ${e.guardian_phone}` : ''}` : '미등록'} span={2}/>
                        </div>
                        {e.medications?.length > 0 && (
                          <div style={{marginTop:14, paddingTop:14, borderTop:'1px solid var(--border-soft)'}}>
                            <div className="muted" style={{fontSize:11, fontWeight:600, marginBottom:8}}>복약 ({e.medications.length}종)</div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
                              {e.medications.map((m, i) => {
                                const name = typeof m === "string" ? m : (m?.name || '이름 없음');
                                return (
                                  <span key={(m && m.id) ?? i} style={{padding:'4px 10px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:6, fontSize:12, fontWeight:500}}>{name}</span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {r ? (
                        <div className="card card-pad">
                          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
                            <div className="h3">방문 보고서 #{r.id}</div>
                            <a href={`/m/report?visit_id=${v.id}`} className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>수정 →</a>
                          </div>
                          <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, marginBottom:12}}>
                            <Vital label="혈압"  value={r.blood_pressure_sys ? `${r.blood_pressure_sys}/${r.blood_pressure_dia}` : '-'} unit="mmHg"/>
                            <Vital label="맥박"  value={r.heart_rate || '-'} unit="bpm"/>
                            <Vital label="체온"  value={r.body_temp || '-'}  unit="°C"/>
                            <Vital label="컨디션" value={r.condition || '-'}/>
                          </div>
                          {r.note && <div style={{padding:12, background:'var(--bg-sub)', borderRadius:8, fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap'}}>{r.note}</div>}
                        </div>
                      ) : v.status === "completed" ? (
                        <div className="card card-pad" style={{display:'flex', alignItems:'center', gap:14}}>
                          <div style={{flex:1}}>
                            <div className="h3">방문 보고서</div>
                            <div className="muted" style={{fontSize:12, marginTop:4}}>아직 작성되지 않았습니다.</div>
                          </div>
                          <a href={`/m/report?visit_id=${v.id}`} className="btn btn-primary btn-md" style={{textDecoration:'none'}}>작성하기 →</a>
                        </div>
                      ) : null}
                    </div>

                    <div style={{display:'flex', flexDirection:'column', gap:14}}>
                      <div className="card card-pad">
                        <div className="h3" style={{marginBottom:10}}>상태 · 액션</div>
                        <div style={{display:'flex', flexDirection:'column', gap:8}}>
                          {v.status === "planned" && (
                            <button type="button" className="btn btn-primary btn-md" onClick={() => {
                              // 체크인 타임스탬프는 정산·감사 근거이므로 오작동 방지용 확인을 둔다.
                              if (!window.confirm(`${e.name} 어르신 방문을 시작(체크인)하시겠습니까?\n지금 시각으로 기록됩니다.`)) return;
                              patchVisit({ status: "in_progress", check_in_at: new Date().toISOString() });
                            }} disabled={busy}>
                              {busy ? "처리 중…" : "방문 시작"}
                            </button>
                          )}
                          {v.status === "in_progress" && (
                            <button type="button" className="btn btn-primary btn-md" onClick={() => {
                              // 완료→예정 되돌리기는 system_admin만 가능 → 일반 사용자에겐 비가역.
                              if (!window.confirm(`${e.name} 어르신 방문을 완료(체크아웃)하시겠습니까?\n완료 처리 후에는 일반 권한으로 되돌릴 수 없습니다.`)) return;
                              patchVisit({ status: "completed", check_out_at: new Date().toISOString() });
                            }} disabled={busy}>
                              {busy ? "처리 중…" : "방문 완료"}
                            </button>
                          )}
                          {v.status === "completed" && (
                            <a href={`/m/report?visit_id=${v.id}`} className="btn btn-primary btn-md" style={{textDecoration:'none', textAlign:'center'}}>보고서 작성 →</a>
                          )}
                          {v.status !== "cancelled" && v.status !== "completed" && (
                            <button type="button" className="btn btn-ghost btn-md" onClick={() => {
                              if (!window.confirm(`${e.name} 어르신 방문을 취소하시겠습니까?`)) return;
                              patchVisit({ status: "cancelled" });
                            }} disabled={busy} style={{color:'var(--rose-500)'}}>
                              방문 취소
                            </button>
                          )}
                          {/* 일정 변경은 예정(planned) 방문만 — 완료·취소·진행중 방문은 시간 변경 불가
                              (/schedule 드래그·백엔드 reschedule_not_allowed와 동일 정책). */}
                          {v.status === 'planned' && (
                            <a href={`/schedule?date=${(v.scheduled_at || '').slice(0,10)}`} className="btn btn-ghost btn-md" style={{textDecoration:'none', textAlign:'center'}}>일정 변경</a>
                          )}
                        </div>
                        <div className="muted" style={{fontSize:11, marginTop:12, lineHeight:1.5}}>
                          현장 체크인/사진 첨부는 모바일 앱에서 진행하는 것을 권장합니다.
                        </div>
                      </div>

                      <div className="card card-pad">
                        <div className="h3" style={{marginBottom:10}}>타임라인</div>
                        <Timeline visit={v}/>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, children, span = 1 }) {
  return (
    <div style={{gridColumn: `span ${span}`}}>
      <div className="muted" style={{fontSize:11, fontWeight:600, marginBottom:2}}>{label}</div>
      <div style={{fontSize:13.5}}>{children ?? value}</div>
    </div>
  );
}

function Vital({ label, value, unit }) {
  return (
    <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:8}}>
      <div className="muted" style={{fontSize:10, fontWeight:600}}>{label}</div>
      <div style={{fontSize:18, fontWeight:800, color:'var(--green-700)', marginTop:2}}>{value}</div>
      {unit && <div className="muted" style={{fontSize:10}}>{unit}</div>}
    </div>
  );
}

function Timeline({ visit }) {
  const items = [
    { l: "예정",     at: visit.scheduled_at, on: true },
    { l: "체크인",   at: visit.check_in_at,  on: !!visit.check_in_at  },
    { l: "체크아웃", at: visit.check_out_at, on: !!visit.check_out_at },
  ];
  return (
    <div style={{display:'flex', flexDirection:'column', gap:10}}>
      {items.map((it, i) => (
        <div key={i} style={{display:'flex', gap:10, alignItems:'flex-start'}}>
          <div style={{width:8, height:8, borderRadius:'50%', marginTop:6, background: it.on ? 'var(--orange-500)' : 'var(--ink-4)', flex:'0 0 auto'}}/>
          <div style={{flex:1}}>
            <div style={{fontSize:12.5, fontWeight:600, color: it.on ? 'var(--ink-1)' : 'var(--ink-3)'}}>{it.l}</div>
            <div className="muted" style={{fontSize:11}}>{it.at ? fmtDateTime(it.at) : "—"}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export { VisitDetail };
